import * as THREE from 'three';
import { leaf, mesh } from './art';

export const BRIDGE = { x: -42, startZ: 50, endZ: 34, length: 18, pushes: 3, duration: .7 };
export function isLake(x: number, z: number): boolean {
  return ((x + 30) / 16.54) ** 2 + ((z - 25) / 11.14) ** 2 < 1;
}

export class LeafBridge {
  readonly model: THREE.Group;
  private readonly stemMarker: THREE.Mesh;
  private elapsed = BRIDGE.duration;
  private fromZ: number;
  private toZ: number;
  steps: number;

  constructor(parent: THREE.Group, steps = 0) {
    this.steps = steps;
    this.fromZ = this.toZ = this.anchorFor(steps);
    this.model = leaf(parent, BRIDGE.x, .18, this.toZ, BRIDGE.length, 3.4, '#9eac62');
    this.stemMarker = mesh(new THREE.RingGeometry(.5, .57, 32), new THREE.MeshBasicMaterial({ color: '#f8e6a3', side: THREE.DoubleSide }), parent, BRIDGE.x, .06, this.toZ + 1.2);
    this.stemMarker.rotation.x = -Math.PI / 2; this.stemMarker.castShadow = false;
    this.stemMarker.visible = !this.solved;
  }
  private anchorFor(steps: number): number { return BRIDGE.startZ + (BRIDGE.endZ - BRIDGE.startZ) * steps / BRIDGE.pushes; }
  get moving(): boolean { return this.elapsed < BRIDGE.duration; }
  get solved(): boolean { return this.steps === BRIDGE.pushes && !this.moving; }
  canPush(x: number, z: number): boolean {
    return !this.moving && this.steps < BRIDGE.pushes && Math.hypot(x - BRIDGE.x, z - (this.toZ + 1.2)) < 2;
  }
  push(x: number, z: number): boolean {
    if (!this.canPush(x, z)) return false;
    this.fromZ = this.model.position.z; this.toZ = this.anchorFor(++this.steps); this.elapsed = 0;
    this.stemMarker.visible = false; return true;
  }
  update(dt: number): boolean {
    const wasMoving = this.moving;
    this.elapsed = Math.min(BRIDGE.duration, this.elapsed + dt);
    const t = this.elapsed / BRIDGE.duration; const smooth = t * t * (3 - 2 * t);
    this.model.position.z = THREE.MathUtils.lerp(this.fromZ, this.toZ, smooth);
    this.stemMarker.position.z = this.toZ + 1.2;
    this.stemMarker.visible = !this.moving && !this.solved;
    return wasMoving && this.solved;
  }
  contains(x: number, z: number): boolean {
    return Math.abs(x - BRIDGE.x) <= .8 && z >= this.model.position.z - BRIDGE.length && z <= this.model.position.z;
  }
  blocked(x: number, z: number): boolean { return isLake(x, z) && !(this.solved && this.contains(x, z)); }
  heightAt(x: number, z: number): number { return this.contains(x, z) ? .2 : 0; }
  static reserved(x: number, z: number): boolean {
    return Math.abs(x - BRIDGE.x) < 3.5 && z > BRIDGE.endZ - BRIDGE.length - 1 && z < BRIDGE.startZ + 2;
  }
}
